/**
 * The views of the money screens, as the address bar holds them. A default
 * is not written into the address.
 */
import { dayOf, dayPlus, marketToday, momentsOfDays } from "@/shared/lib/days";
import { minorDigits, parseAmount } from "@/shared/lib/format";
import { codeOf, currencyOf, idOf, marketOf, text } from "@/shared/lib/search";

import type {
  AdjustmentsFilter,
  BankCallsFilter,
  LedgerFilter,
  PayoutsFilter,
  ReportFilter,
  SummarySpan,
  TopupCountsFilter,
  TopupsFilter,
  WalletCountsFilter,
  WalletEntriesFilter,
  WalletsFilter,
} from "../api/queries";
import {
  BANK_CALL_OUTCOMES,
  BANK_METHODS,
  PAYOUT_OWNERS,
  REPORT_GROUPS,
  TOPUP_CHANNELS,
  TOPUP_STATES,
  WALLET_SORTS,
  WALLET_STATES,
  type BankCallOutcome,
  type PayoutOwner,
  type ReportGroup,
  type TopupProvider,
  type TopupState,
  type WalletSort,
  type WalletState,
} from "../api/types";

// ---- what several lists are narrowed by ----

/** A span of the market's days and a span of sums, as the address holds them. */
export interface SpanSearch {
  /** The first day, `YYYY-MM-DD`, by the market's clock. */
  from?: string;
  /** The last day, the whole of it. */
  to?: string;
  /** The least sum, in the currency's whole units: `50000`. */
  min?: number;
  /** The most. */
  max?: number;
}

/** The currency sums are typed in where a list does not say its own. */
const DEFAULT_CURRENCY = "UZS";

/**
 * A sum as typed into a filter (`50 000`, `50000,5`) or as the address holds
 * it: a number of the currency's whole units, with two digits after the
 * point at most. Anything else is no sum.
 */
function sumOf(value: unknown): number | undefined {
  const typed = text(value)?.replace(/\s/g, "").replace(",", ".");
  if (typed === undefined || !/^\d{1,12}(\.\d{1,2})?$/.test(typed)) return undefined;
  return Number(typed);
}

function parseSpan(raw: Readonly<Record<string, unknown>>): SpanSearch {
  const span: SpanSearch = {};
  const from = dayOf(raw["from"]);
  if (from !== undefined) span.from = from;
  const to = dayOf(raw["to"]);
  if (to !== undefined) span.to = to;
  const min = sumOf(raw["min"]);
  if (min !== undefined) span.min = min;
  const max = sumOf(raw["max"]);
  if (max !== undefined) span.max = max;
  return span;
}

/** A span's days and sums as a filter bar holds them: every value the letters of its field. */
export function spanValues(
  span: SpanSearch,
): Record<"from" | "to" | "min" | "max", string | undefined> {
  return {
    from: span.from,
    to: span.to,
    min: span.min === undefined ? undefined : String(span.min),
    max: span.max === undefined ? undefined : String(span.max),
  };
}

/** The sums of a span in minor units: what was typed in whole units of the currency. */
function sumsOf(
  span: SpanSearch,
  currency: string = DEFAULT_CURRENCY,
): { amount_min?: number; amount_max?: number } {
  const digits = minorDigits(currency);
  const minor = (sum: number | undefined) =>
    sum === undefined ? null : parseAmount(String(sum), digits);
  const sums: { amount_min?: number; amount_max?: number } = {};
  const min = minor(span.min);
  if (min !== null) sums.amount_min = min;
  const max = minor(span.max);
  // The services refuse a most below the least: it is not asked, the person is still typing.
  if (max !== null && (min === null || max >= min)) sums.amount_max = max;
  return sums;
}

/** The fewest characters hg-billing looks a memo or a top-up up by; fewer is `422 out_of_range`. */
export const MEMO_SEARCH_MIN = 3;

/** What is typed to find a memo, once it is long enough to be asked. */
function memoOf(q: string | undefined): string | undefined {
  const words = q?.trim() ?? "";
  return words.length >= MEMO_SEARCH_MIN ? words.slice(0, 100) : undefined;
}

/** Whether a search is typed and still too short to be asked. */
export function memoTooShort(q: string | undefined): boolean {
  const words = q?.trim() ?? "";
  return words !== "" && words.length < MEMO_SEARCH_MIN;
}

// ---- wallets ----

export const DEFAULT_WALLET_SORT: WalletSort = "balance";

/** Whose wallets: the specialists approved to work (`yes`), or everybody else (`no`). */
export const WALLET_APPROVED = ["yes", "no"] as const;
export type WalletApproved = (typeof WALLET_APPROVED)[number];

export interface WalletsSearch {
  state?: WalletState;
  sort?: WalletSort;
  currency?: string;
  approved?: WalletApproved;
}

/** Reads the address. Anything it does not know is dropped, never trusted. */
export function parseWalletsSearch(raw: Readonly<Record<string, unknown>>): WalletsSearch {
  const search: WalletsSearch = {};
  const state = WALLET_STATES.find((known) => known === raw["state"]);
  if (state !== undefined) search.state = state;
  const sort = WALLET_SORTS.find((known) => known === raw["sort"]);
  if (sort !== undefined && sort !== DEFAULT_WALLET_SORT) search.sort = sort;
  const currency = currencyOf(raw["currency"]);
  if (currency !== undefined) search.currency = currency;
  const approved = WALLET_APPROVED.find((known) => known === raw["approved"]);
  if (approved !== undefined) search.approved = approved;
  return search;
}

/**
 * What the list asks `GET /v2/admin/wallets` for. `q` is what it is searched
 * for (part of a specialist's name, or the start of her number): personal
 * data, which comes from the screen and is never in the address
 * (`@/shared/lib/private-search`).
 */
export function walletsFilter(search: WalletsSearch, q?: string): WalletsFilter {
  const filter: WalletsFilter = {};
  if (search.state !== undefined) filter.state = search.state;
  if (q !== undefined) filter.q = q;
  if (search.sort !== undefined) filter.sort = search.sort;
  if (search.currency !== undefined) filter.currency = search.currency;
  if (search.approved !== undefined) filter.approved = search.approved === "yes";
  return filter;
}

/** What the tabs are counted with: the list's filters but the state, which each tab is. */
export function walletCountsFilter(filter: WalletsFilter): WalletCountsFilter {
  const counts: WalletCountsFilter = {};
  if (filter.q !== undefined) counts.q = filter.q;
  if (filter.currency !== undefined) counts.currency = filter.currency;
  if (filter.approved !== undefined) counts.approved = filter.approved;
  return counts;
}

/** The tabs of the list: every wallet, then one a state. */
export const WALLET_TABS = ["all", ...WALLET_STATES] as const;
export type WalletTab = (typeof WALLET_TABS)[number];

export function walletTabOf(search: WalletsSearch): WalletTab {
  return search.state ?? "all";
}

export function withWalletTab(search: WalletsSearch, tab: WalletTab): WalletsSearch {
  const { state: _, ...rest } = search;
  return tab === "all" ? rest : { ...rest, state: tab };
}

// ---- one wallet ----

export interface WalletSearch extends SpanSearch {
  /** The lines of one type only. */
  type?: string;
}

export function parseWalletSearch(raw: Readonly<Record<string, unknown>>): WalletSearch {
  const type = codeOf(raw["type"]);
  return { ...(type === undefined ? {} : { type }), ...parseSpan(raw) };
}

/**
 * What a wallet's history is asked for. `q` is a part of staff's memo: words
 * about a person, kept by the screen and never in the address.
 */
export function walletEntriesFilter(
  search: WalletSearch,
  q?: string,
  currency?: string,
): WalletEntriesFilter {
  const filter: WalletEntriesFilter = {};
  if (search.type !== undefined) filter.entry_type = search.type;
  const moments = momentsOfDays(search.from, search.to);
  if (moments.from !== undefined) filter.occurred_from = moments.from;
  if (moments.to !== undefined) filter.occurred_to = moments.to;
  Object.assign(filter, sumsOf(search, currency));
  const memo = memoOf(q);
  if (memo !== undefined) filter.q = memo;
  return filter;
}

/** The span a wallet's totals are asked for: the days of its history's filter. */
export function summarySpan(search: WalletSearch): SummarySpan {
  const moments = momentsOfDays(search.from, search.to);
  const span: SummarySpan = {};
  if (moments.from !== undefined) span.occurred_from = moments.from;
  if (moments.to !== undefined) span.occurred_to = moments.to;
  return span;
}

// ---- top-ups ----

/** The tabs of the top-ups list: every one, the three that wait for a staff member, then one a state. */
export const TOPUP_TABS = [
  "all",
  "in_suspense",
  "contested",
  "to_review",
  "performed",
  "created",
  "failed",
  "cancelled",
  "reversed",
] as const;
export type TopupTab = (typeof TOPUP_TABS)[number];

export interface TopupsSearch extends SpanSearch {
  state?: TopupState;
  /** Only the ones a payment system later said had failed. */
  contested?: boolean;
  /** Only the card top-ups marked for staff and not dealt with yet. */
  review?: boolean;
  provider?: TopupProvider;
  /** The top-ups of one specialist. */
  specialist?: string;
}

export function parseTopupsSearch(raw: Readonly<Record<string, unknown>>): TopupsSearch {
  const search: TopupsSearch = {};
  const state = TOPUP_STATES.find((known) => known === raw["state"]);
  if (state !== undefined) search.state = state;
  if (raw["contested"] === true || raw["contested"] === "true") search.contested = true;
  if (raw["review"] === true || raw["review"] === "true") search.review = true;
  const provider = TOPUP_CHANNELS.find((known) => known === raw["provider"]);
  if (provider !== undefined) search.provider = provider;
  const specialist = idOf(raw["specialist"]);
  if (specialist !== undefined) search.specialist = specialist;
  return { ...search, ...parseSpan(raw) };
}

/**
 * What the list asks `GET /v2/admin/topups` for. `q` is a part of what a
 * top-up is known by (a payment system's id, the account the payer typed):
 * kept by the screen and never in the address; asked from three characters on.
 */
export function topupsFilter(search: TopupsSearch, q?: string): TopupsFilter {
  const filter: TopupsFilter = {};
  if (search.state !== undefined) filter.state = search.state;
  if (search.contested === true) filter.contested = true;
  if (search.review === true) filter.needs_review = true;
  if (search.provider !== undefined) filter.provider = search.provider;
  if (search.specialist !== undefined) filter.specialist_id = search.specialist;
  const moments = momentsOfDays(search.from, search.to);
  if (moments.from !== undefined) filter.created_from = moments.from;
  if (moments.to !== undefined) filter.created_to = moments.to;
  const typed = memoOf(q);
  if (typed !== undefined) filter.q = typed;
  return { ...filter, ...sumsOf(search) };
}

/** What the tabs are counted with: everything the list is narrowed by but the tab itself. */
export function topupCountsFilter(filter: TopupsFilter): TopupCountsFilter {
  const { state: _, contested: __, needs_review: ___, ...rest } = filter;
  return rest;
}

export function topupTabOf(search: TopupsSearch): TopupTab {
  if (search.contested === true) return "contested";
  if (search.review === true) return "to_review";
  return search.state ?? "all";
}

/** Another tab: the payment system and the specialist chosen are kept. */
export function withTopupTab(search: TopupsSearch, tab: TopupTab): TopupsSearch {
  const { state: _, contested: __, review: ___, ...rest } = search;
  if (tab === "all") return rest;
  if (tab === "contested") return { ...rest, contested: true };
  if (tab === "to_review") return { ...rest, review: true };
  return { ...rest, state: tab };
}

// ---- adjustments ----

/**
 * The tabs of the adjustments: the queue first, then what was decided (turned
 * down by somebody else apart from withdrawn by its maker), then all.
 */
export const ADJUSTMENT_TABS = ["pending", "posted", "rejected", "withdrawn", "all"] as const;
export type AdjustmentTab = (typeof ADJUSTMENT_TABS)[number];
export const DEFAULT_ADJUSTMENT_TAB: AdjustmentTab = "pending";

export interface AdjustmentsSearch {
  tab?: AdjustmentTab;
  /** The adjustments of one wallet. */
  provider?: string;
}

export function parseAdjustmentsSearch(raw: Readonly<Record<string, unknown>>): AdjustmentsSearch {
  const search: AdjustmentsSearch = {};
  const tab = ADJUSTMENT_TABS.find((known) => known === raw["tab"]);
  if (tab !== undefined && tab !== DEFAULT_ADJUSTMENT_TAB) search.tab = tab;
  const provider = idOf(raw["provider"]);
  if (provider !== undefined) search.provider = provider;
  return search;
}

export function adjustmentTabOf(search: AdjustmentsSearch): AdjustmentTab {
  return search.tab ?? DEFAULT_ADJUSTMENT_TAB;
}

/** What the list asks `GET /v2/admin/ledger/adjustments` for. */
export function adjustmentsFilter(search: AdjustmentsSearch): AdjustmentsFilter {
  const filter: AdjustmentsFilter = {};
  const tab = adjustmentTabOf(search);
  if (tab !== "all") filter.status = tab;
  if (search.provider !== undefined) filter.provider_id = search.provider;
  return filter;
}

export function withAdjustmentTab(
  search: AdjustmentsSearch,
  tab: AdjustmentTab,
): AdjustmentsSearch {
  const { tab: _, ...rest } = search;
  return tab === DEFAULT_ADJUSTMENT_TAB ? rest : { ...rest, tab };
}

// ---- payouts ----

/**
 * The tabs of the payouts: the two queues first (to approve, to send), then
 * what became of the rest, then every one. One status a tab: hg-billing
 * lists one status at a time, and counts each.
 */
export const PAYOUT_TABS = [
  "requested",
  "approved",
  "paid",
  "rejected",
  "cancelled",
  "all",
] as const;
export type PayoutTab = (typeof PAYOUT_TABS)[number];
export const DEFAULT_PAYOUT_TAB: PayoutTab = "requested";

export interface PayoutsSearch {
  tab?: PayoutTab;
  /** A specialist's (`provider`) or a referral partner's (`partner`). */
  owner?: PayoutOwner;
  /** One market's: `UZ`. */
  market?: string;
  /** The payouts of one wallet. */
  provider?: string;
}

export function parsePayoutsSearch(raw: Readonly<Record<string, unknown>>): PayoutsSearch {
  const search: PayoutsSearch = {};
  const tab = PAYOUT_TABS.find((known) => known === raw["tab"]);
  if (tab !== undefined && tab !== DEFAULT_PAYOUT_TAB) search.tab = tab;
  const owner = PAYOUT_OWNERS.find((known) => known === raw["owner"]);
  if (owner !== undefined) search.owner = owner;
  const market = marketOf(raw["market"]);
  if (market !== undefined) search.market = market;
  const provider = idOf(raw["provider"]);
  if (provider !== undefined) search.provider = provider;
  return search;
}

export function payoutTabOf(search: PayoutsSearch): PayoutTab {
  return search.tab ?? DEFAULT_PAYOUT_TAB;
}

/** What the list asks `GET /v2/admin/payouts` for. */
export function payoutsFilter(search: PayoutsSearch): PayoutsFilter {
  const filter: PayoutsFilter = {};
  const tab = payoutTabOf(search);
  if (tab !== "all") filter.status = tab;
  if (search.owner !== undefined) filter.owner_type = search.owner;
  if (search.market !== undefined) filter.market_code = search.market;
  if (search.provider !== undefined) filter.provider_id = search.provider;
  return filter;
}

/** Another tab: the filters chosen are kept. */
export function withPayoutTab(search: PayoutsSearch, tab: PayoutTab): PayoutsSearch {
  const { tab: _, ...rest } = search;
  return tab === DEFAULT_PAYOUT_TAB ? rest : { ...rest, tab };
}

// ---- the ledger ----

export interface LedgerSearch extends SpanSearch {
  /** The entries of one type. */
  type?: string;
  /** The entries that touch an account of this kind. */
  kind?: string;
}

export function parseLedgerSearch(raw: Readonly<Record<string, unknown>>): LedgerSearch {
  const search: LedgerSearch = {};
  const type = codeOf(raw["type"]);
  if (type !== undefined) search.type = type;
  const kind = codeOf(raw["kind"]);
  if (kind !== undefined) search.kind = kind;
  return { ...search, ...parseSpan(raw) };
}

/**
 * What the entries are asked for. `q` is a part of staff's memo, kept by the
 * screen and never in the address; it is asked from three characters on.
 */
export function ledgerFilter(search: LedgerSearch, q?: string): LedgerFilter {
  const filter: LedgerFilter = {};
  if (search.type !== undefined) filter.entry_type = search.type;
  if (search.kind !== undefined) filter.kind = search.kind;
  const moments = momentsOfDays(search.from, search.to);
  if (moments.from !== undefined) filter.occurred_from = moments.from;
  if (moments.to !== undefined) filter.occurred_to = moments.to;
  Object.assign(filter, sumsOf(search));
  const memo = memoOf(q);
  if (memo !== undefined) filter.q = memo;
  return filter;
}

// ---- the report ----

export const DEFAULT_REPORT_GROUP: ReportGroup = "day";

/** How many days the report opens on: the last thirty, today included. */
export const REPORT_DAYS = 30;

export interface ReportSearch {
  /** The first day; left out, thirty days back from the last. */
  from?: string;
  /** The last day, itself included; left out, today by the market's clock. */
  to?: string;
  group?: ReportGroup;
  market?: string;
}

export function parseReportSearch(raw: Readonly<Record<string, unknown>>): ReportSearch {
  const search: ReportSearch = {};
  const from = dayOf(raw["from"]);
  if (from !== undefined) search.from = from;
  const to = dayOf(raw["to"]);
  if (to !== undefined) search.to = to;
  const group = REPORT_GROUPS.find((known) => known === raw["group"]);
  if (group !== undefined && group !== DEFAULT_REPORT_GROUP) search.group = group;
  const market = marketOf(raw["market"]);
  if (market !== undefined) search.market = market;
  return search;
}

/** What `GET /v2/admin/money/report` is asked: both days always, the ones the address leaves out worked out from today. */
export function reportFilter(search: ReportSearch, now: Date = new Date()): ReportFilter {
  const to = search.to ?? marketToday(now);
  const from = search.from ?? dayPlus(to, 1 - REPORT_DAYS);
  const filter: ReportFilter = { from: from > to ? to : from, to };
  if (search.group !== undefined) filter.group = search.group;
  if (search.market !== undefined) filter.market = search.market;
  return filter;
}

// ---- the calls to the bank ----

export interface BankCallsSearch {
  method?: string;
  outcome?: BankCallOutcome;
  /** The calls made about one top-up. */
  topup?: string;
  from?: string;
  to?: string;
}

export function parseBankCallsSearch(raw: Readonly<Record<string, unknown>>): BankCallsSearch {
  const search: BankCallsSearch = {};
  const method = BANK_METHODS.find((known) => known === raw["method"]);
  if (method !== undefined) search.method = method;
  const outcome = BANK_CALL_OUTCOMES.find((known) => known === raw["outcome"]);
  if (outcome !== undefined) search.outcome = outcome;
  const topup = idOf(raw["topup"]);
  if (topup !== undefined) search.topup = topup;
  const from = dayOf(raw["from"]);
  if (from !== undefined) search.from = from;
  const to = dayOf(raw["to"]);
  if (to !== undefined) search.to = to;
  return search;
}

export function bankCallsFilter(search: BankCallsSearch): BankCallsFilter {
  const filter: BankCallsFilter = {};
  if (search.method !== undefined) filter.method = search.method;
  if (search.outcome !== undefined) filter.outcome = search.outcome;
  if (search.topup !== undefined) filter.topup_id = search.topup;
  const moments = momentsOfDays(search.from, search.to);
  if (moments.from !== undefined) filter.called_from = moments.from;
  if (moments.to !== undefined) filter.called_to = moments.to;
  return filter;
}
